import { AfterViewInit, Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { merge, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

import { AlertsCenterService } from 'src/app/core/services/alerts-center.service';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { DialogService } from 'src/app/services/dialog.service';
import { UsersService } from 'src/app/services/users.service';
import { UsersDataSource } from 'src/app/services/users.datasource';
import { Toolbar, ToolbarEvent } from 'src/app/shared/components/toolbar/toolbar';
import { TokenService } from 'src/app/services/token.service';


interface UserSearchableDto {
  codiceFiscale: string | null;
  cognome: string | null;
  nome: string | null;
  associato?: boolean;
  enteCode?: string;
}


interface UserDto {
  codiceFiscale: string;
  cognome: string;
  nome: string;
  codiceUfficio: string;
}


//interfaccia per la risposta ACL del backend
interface AclResponse {
  acl: string;
  ente: string;
  nomeEnte: string;
}


@Component({
  selector: 'app-gestione-utenti',
  templateUrl: './gestione-utenti.component.html',
  styleUrls: ['./gestione-utenti.component.css']
})
export class GestioneUtentiComponent implements AfterViewInit, OnInit {

  arrowFlag: boolean = false;
  risultatiPerPagina: number = 10;
  dataSource: UsersDataSource;
  acl: string = '';
  enteCode: string | null = null;
  nomeEnte: string | null = null;

  userSearchableDto: UserSearchableDto = {
    codiceFiscale: null,
    cognome: null,
    nome: null
  };

  public form: FormGroup;

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  public searchFilters: UserSearchableDto;
  openToggle: number = -1;
  isLoading: boolean = false;


  displayedColumns: string[] = ['codiceFiscale', 'cognome', 'nome', 'associazione', 'azioni'];

  constructor(
    private router: Router,
    private alertsCenterService: AlertsCenterService,
    private authenticationService: AuthenticationService,
    private dialogService: DialogService,
    private usersService: UsersService,
    private http: HttpClient,
    private tokenService: TokenService
  ) { }


  ngOnInit(): void {
    this.buildForm();
    this.dataSource = new UsersDataSource(this.usersService);

    this.dataSource.errors$.subscribe(res => {
      if (res) this.alertsCenterService.showAlert({
        message: `Errore durante il caricamento degli utenti`,
        type: 'danger',
        autoClosable: false
      });
    });

    this.acl = sessionStorage.getItem("acl"); //recupera l'ACL dalla sessione
    this.getAclUtente();
  }

  private getAclUtente(): void {
    this.tokenService.getJwtMyProfile().subscribe(token => {
      const headers = new HttpHeaders({
        'X-MyProfile-Token': token || ''
      });
      this.http.get<AclResponse>(`${environment.urls.base}/utente/acl-utente`, { headers }).subscribe(
        response => {
          this.enteCode = response.ente;
          this.nomeEnte = response.nomeEnte;
          this.ricercaUtenti();
        },
        error => {
          console.error("Errore nel recupero dell'ACL dell'utente:", error);
          this.alertsCenterService.showAlert({
            message: `Errore nel recupero del codice Ente.`,
            type: 'danger',
            autoClosable: false
          });
        }
      );
    });
  }



  ngAfterViewInit() {
    setTimeout(() => {
      this.sort.direction = "asc"; //ordina per cognome in ordine ascendente di default
      this.sort.active = "cognome";

      this.form.valueChanges.subscribe(() => this.paginator.pageIndex = 0);
      this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);

      merge(this.sort.sortChange, this.paginator.page)
        .pipe(
          tap(() => {
            this.ricercaUtenti();
          }))
        .subscribe();


      this.dataSource.counter$
        .pipe(
          tap((count) => {
            this.paginator.length = count;
          })
        )
        .subscribe();
    });
  }


  private buildForm(): void {
    let group = {
      codiceFiscale: new FormControl(null, null),
      cognome: new FormControl(null, null),
      nome: new FormControl(null, null),
      associato: new FormControl(null, null),
    };
    this.form = new FormGroup(group);
  }


  public ricercaUtenti(): void {
    this.searchFilters = {
      codiceFiscale: this.form.controls['codiceFiscale'].value !== "" ? this.form.controls['codiceFiscale'].value : null,
      cognome: this.form.controls['cognome'].value !== "" ? this.form.controls['cognome'].value : null,
      nome: this.form.controls['nome'].value !== "" ? this.form.controls['nome'].value : null,
      associato: this.form.controls['associato'].value,
      enteCode: this.enteCode
    };

    if (this.searchFilters.associato === null) {
      delete this.searchFilters.associato;
    }

    this.dataSource.loadUsers(
      this.searchFilters,
      this.sort.active + "," + this.sort.direction,
      this.paginator.pageIndex,
      this.paginator.pageSize
    );
  }


  public onNuovo(): void {
    this.router.navigate(['/gestione-utenti/nuovo']);
  }


  public onReset(): void {
    this.userSearchableDto = {
      codiceFiscale: null,
      cognome: null,
      nome: null,
      associato: null,
    };
    this.form.reset();
    this.ricercaUtenti();
  }


  public generateToolBar(): Toolbar {
    let toolbar: Toolbar;
    toolbar = { toolbarCss: 'toolbar-vertical', actions: [] };


    toolbar.actions.push(
      {
        btnTitle: 'Elimina',
        btnIconClass: 'delete',
        btnActionEvent: 'remove',
        btnCssClass: 'btn btn-primary'
      }
    );
    return toolbar;
  }


  public handleToolbarEvent(event: ToolbarEvent): void {
    if (event.event === 'remove') {
      this.openDeleteConfirmDialog(event.data);
      this.openToggle = -1;
    }
  }


  public handleToolbarClick(event: Event, index: number): void {
      event.stopPropagation();
      this.openToggle = (this.openToggle === index) ? -1 : index;
  }


  //metodo per aprire il dialog di conferma eliminazione
  openDeleteConfirmDialog(user: any) {
    const enteName = this.nomeEnte || 'Ente non disponibile';
    let title = `Cancellazione utente ${user.nome} ${user.cognome}`;
    let message = `Per rimuovere un utente è necessario inviare una mail a call.center@regione.veneto.it con il seguente testo:

<b>Oggetto:</b> S76 - Gestione Emergenze: richiesta disabilitazione utente in ambiente MyProfile

<b>Testo:</b> Si richiede di rimuovere l'associazione tra l'utente ${user.cognome} ${user.nome} (codice fiscale ${user.codiceFiscale}) e l'Ente ${enteName} per quanto riguarda la gestione di S76 - Gestione Emergenze.`;

    this.dialogService.openDialog(title, message).subscribe(
      result => {
        if (result) {
          this.cancellaUtente(user);
        }
      }
    );
  }


  public cancellaUtente(user: any): void {
    const userCode = user.codiceFiscale;
    const officeCode = this.enteCode;

    if (!officeCode) {
      console.error("Errore: Impossibile cancellare l'utente. Codice ente non disponibile.");
      this.alertsCenterService.showAlert({
        message: `Errore: codice Ente non disponibile per la cancellazione. Riprova.`,
        type: 'danger',
        autoClosable: false,
      });
      this.isLoading = false;
      return;
    }

    this.isLoading = true;

    this.usersService.deleteUser(userCode, officeCode).subscribe(
      results => {
        this.alertsCenterService.showAlert({
          message: `Utente cancellato con successo`,
          type: 'success',
          autoClosable: false
        });
        this.isLoading = false;
        this.ricercaUtenti();
      },
      err => {
        this.alertsCenterService.showAlert({
          message: err.error.esitoServizio.descrizioneEsito,
          type: 'danger',
          autoClosable: false,
        });
        this.isLoading = false;
      }
    );
  }


  @HostListener('document:click', ['$event'])
  documentClick(event: Event) {
    const target = event.target as HTMLElement;

    if (!target.closest('.toolbar-container') && !target.closest('.toolbar-icon')) {
       this.openToggle = -1;
    }
  }
}

